/**
 * Number formatting helpers for marketing surfaces.
 *
 * These used to accompany a `getImpact()` client that read /public/impact —
 * live reach/engagement totals plus a day-by-day cumulative growth series.
 * That endpoint was removed 2026-08-02: the site no longer publishes aggregate
 * numbers, because polling them over time exposed our growth cadence, our
 * activation rate and (via AI-conversation volume) our cost profile. The
 * formatters stay because static marketing figures still use them.
 */

/**
 * Compact human number: 1932133 -> "1.9M", 24504 -> "24.5K", 950 -> "950".
 * Deliberately rounds DOWN so we never overstate.
 */
export function compact(n: number): string {
  if (n == null || isNaN(n)) return '0';
  if (n < 1000) return String(Math.floor(n));
  if (n < 1_000_000) {
    const k = Math.floor(n / 100) / 10; // one decimal, rounded down
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  const m = Math.floor(n / 100_000) / 10;
  return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
}

/**
 * "N+" rounded down to a clean floor for soft/over-counted figures (schools).
 * 1955 -> "1,000+", 4258 -> "4,000+".
 */
export function softFloor(n: number): string {
  if (n < 100) return String(n);
  if (n < 1000) return `${Math.floor(n / 100) * 100}+`;
  const thousands = Math.floor(n / 1000);
  return `${thousands.toLocaleString()},000+`;
}
